import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Images, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, API, errText } from "@/lib/api";
import { OrnamentDivider, FloralBranch } from "@/components/Floral";
import axios from "axios";

function getSessionId() {
  let sid = localStorage.getItem("photo_session_id");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("photo_session_id", sid);
  }
  return sid;
}

export default function PhotoUpload() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [justUploaded, setJustUploaded] = useState(0);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const sessionId = getSessionId();

  useEffect(() => {
    api
      .get(`/photos/table/${code}`, { params: { session_id: sessionId } })
      .then(({ data }) => setInfo(data))
      .catch(() => setNotFound(true));
  }, [code, sessionId]);

  const onSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setFiles(picked.map((f) => ({ file: f, url: URL.createObjectURL(f) })));
    setJustUploaded(0);
    e.target.value = "";
  };

  const clear = () => {
    files.forEach((f) => URL.revokeObjectURL(f.url));
    setFiles([]);
  };

  const removeAt = (idx) => {
    URL.revokeObjectURL(files[idx].url);
    setFiles((fs) => fs.filter((_, i) => i !== idx));
  };

  const upload = async () => {
    let done = 0;
    let lastCount = info.uploaded;
    for (let i = 0; i < files.length; i++) {
      const form = new FormData();
      form.append("file", files[i].file);
      form.append("session_id", sessionId);
      setProgress({ index: i + 1, total: files.length, pct: 0 });
      try {
        const { data } = await axios.post(`${API}/photos/upload/${code}`, form, {
          onUploadProgress: (ev) => setProgress({ index: i + 1, total: files.length, pct: Math.round((ev.loaded / (ev.total || 1)) * 100) }),
        });
        lastCount = data.uploaded;
        done += 1;
      } catch (e) {
        toast.error(`${files[i].file.name}: ${errText(e)}`);
      }
    }
    setInfo((i) => ({ ...i, uploaded: lastCount }));
    setProgress(null);
    clear();
    if (done > 0) setJustUploaded(done);
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <p className="label-caps text-sm" style={{ color: "var(--ink)" }}>
          Ogiltig QR-kod
        </p>
      </div>
    );
  }

  if (!info) return <div className="min-h-screen" />;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">
      <FloralBranch className="absolute top-8 -left-10 w-44 opacity-60 pointer-events-none" />
      <FloralBranch flip className="absolute bottom-8 -right-10 w-44 opacity-60 pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm text-center relative"
      >
        <p className="label-caps text-xs" style={{ color: "var(--gold)" }}>
          Fånga ögonblicket
        </p>
        <p className="font-script text-5xl mt-5 leading-snug" style={{ color: "var(--ink)" }}>
          Hjälp oss minnas dagen
        </p>
        <p className="font-serif-d italic text-lg mt-3" style={{ color: "var(--ink-soft)" }}>
          genom era ögon.
        </p>
        <OrnamentDivider className="my-8" />

        <>
            <p className="label-caps text-[0.7rem]" style={{ color: "var(--ink)" }} data-testid="photos-uploaded">
              {info.uploaded === 0 ? "Dela så många foton ni vill" : `${info.uploaded} ${info.uploaded === 1 ? "foto delat" : "foton delade"}`}
            </p>

            <AnimatePresence mode="wait">
              {justUploaded > 0 && files.length === 0 && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  className="mt-6 flex flex-col items-center gap-2"
                  data-testid="upload-success-msg"
                >
                  <Check className="w-5 h-5" style={{ color: "var(--gold)" }} />
                  <p className="font-serif-d italic text-xl" style={{ color: "var(--ink)" }}>
                    {justUploaded === 1 ? "Vackert. Ett ögonblick förevigat." : `Vackert. ${justUploaded} ögonblick förevigade.`}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {files.length > 0 ? (
              <div className="mt-8">
                {files.length === 1 ? (
                  <img
                    src={files[0].url}
                    alt="Förhandsvisning"
                    className="w-full max-h-80 object-contain mx-auto"
                    style={{ border: "1px solid var(--gold-soft)", padding: "6px", backgroundColor: "#fff" }}
                  />
                ) : (
                  <div className="grid grid-cols-3 gap-2" data-testid="photo-preview-grid">
                    {files.map((f, i) => (
                      <div key={f.url} className="relative aspect-square" style={{ border: "1px solid var(--gold-soft)", padding: "3px", backgroundColor: "#fff" }}>
                        <img src={f.url} alt="" className="w-full h-full object-cover" />
                        {progress === null && (
                          <button
                            type="button"
                            onClick={() => removeAt(i)}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: "var(--ink)", color: "var(--paper)" }}
                            aria-label="Ta bort"
                            data-testid={`remove-photo-${i}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {progress !== null ? (
                  <div className="mt-6">
                    <div className="h-px w-full" style={{ backgroundColor: "var(--gold-soft)" }}>
                      <div
                        className="h-px transition-all duration-300"
                        style={{ width: `${((progress.index - 1) * 100 + progress.pct) / progress.total}%`, backgroundColor: "var(--ink)" }}
                      />
                    </div>
                    <p className="label-caps text-[0.6rem] mt-3" style={{ color: "var(--ink-soft)" }} data-testid="upload-progress">
                      {progress.total > 1 ? `Laddar upp ${progress.index} av ${progress.total}… ${progress.pct}%` : `Laddar upp… ${progress.pct}%`}
                    </p>
                  </div>
                ) : (
                  <div className="flex justify-center gap-3 mt-6">
                    <button className="btn-invite btn-solid" onClick={upload} data-testid="upload-photo-btn">
                      {files.length > 1 ? `Ladda upp (${files.length})` : "Ladda upp"}
                    </button>
                    <button className="btn-invite" onClick={clear} data-testid="cancel-photo-btn">
                      Avbryt
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3 mt-8">
                <button className="btn-invite btn-solid w-full py-4" onClick={() => cameraRef.current?.click()} data-testid="take-photo-btn">
                  <Camera className="w-4 h-4" /> Ta ett foto
                </button>
                <button className="btn-invite w-full py-4" onClick={() => galleryRef.current?.click()} data-testid="choose-photo-btn">
                  <Images className="w-4 h-4" /> Välj från bildbiblioteket
                </button>
              </div>
            )}

            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onSelect} data-testid="camera-input" />
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onSelect} data-testid="gallery-input" />
          </>

        <div className="hairline w-20 mx-auto mt-12" />
        <p className="font-script text-2xl mt-5" style={{ color: "var(--gold)" }}>
          Sarah &amp; Rafid
        </p>
      </motion.div>
    </div>
  );
}
